import React from 'react';
import { ChevronLeft } from 'shared/assets/icons';
import { NewHabitRow } from 'shared/components/connected/new-habit-row';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import { Collapse } from 'shared/components/ui/collapse';
import {
  Colors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { ListSectionDateHeader } from 'shared/components/ui/list-section-date-header';
import { ListSectionMonthHeader } from 'shared/components/ui/list-section-month-header';
import { ListSectionYearHeader } from 'shared/components/ui/list-section-year-header';
import { useToggle } from 'shared/hooks/use-toggle';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { TaskPriorityOptions } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';

import { GoalHeader } from './goal-header';
import * as Styled from './task-card-task-section-layout.style';

export enum Headers {
  Date = 'date',
  Month = 'month',
  Year = 'year',
  Goal = 'goal',
}

export type TaskSectionLayoutProps = {
  customHeader?: React.ReactNode;
  date?: Timestamp;
  startTime?: number | null;
  duration?: number | null;
  goalId?: ID;
  lifeAreaId?: ID;
  parentIds?: ID[] | null;
  parentChildIds?: ID[] | null;
  priority?: TaskPriorityOptions;
  tagIds?: ID[];
  headerToDisplay?: Headers;
  headerIsLink?: boolean;
  onHeader?: () => void;
  goals: Goal[];
  lifeAreas: LifeArea[];
  headerHasImage?: boolean;
  canCreateTask?: boolean;
  canCreateHabit?: boolean;
  canCollapse?: boolean;
  hideMeta?: boolean;
  newTaskInputRef?: React.RefObject<HTMLDivElement>;
  onPremium: () => void;
  children: React.ReactNode;
};

export const TaskCardTaskSectionLayout: React.FC<TaskSectionLayoutProps> = ({
  date,
  startTime,
  duration,
  goalId,
  lifeAreaId,
  parentIds,
  parentChildIds,
  priority,
  tagIds,
  customHeader,
  headerToDisplay,
  headerIsLink,
  onHeader,
  goals,
  lifeAreas,
  headerHasImage = headerToDisplay === Headers.Goal,
  canCreateTask,
  canCreateHabit,
  canCollapse,
  hideMeta,
  newTaskInputRef,
  onPremium,
  children,
}) => {
  const [collapseOpen, toggleCollapse] = useToggle(true);

  const CollapseContainer = canCollapse ? Collapse : React.Fragment;
  const collapseProps = canCollapse ? { isOpen: collapseOpen } : {};

  const hasHeader = !!customHeader || !!headerToDisplay;

  return (
    <>
      <Styled.HeaderContainer
        $hasHeader={hasHeader}
        $canCollapse={!!canCollapse}
        onClick={onHeader ?? (canCollapse ? toggleCollapse : undefined)}
      >
        {customHeader}
        {!customHeader && !!date && (
          <>
            {headerToDisplay === Headers.Date && (
              <ListSectionDateHeader date={date} />
            )}
            {headerToDisplay === Headers.Month && (
              <ListSectionMonthHeader date={date} />
            )}
            {headerToDisplay === Headers.Year && (
              <ListSectionYearHeader date={date} />
            )}
          </>
        )}
        {!customHeader && headerToDisplay === Headers.Goal && (
          <GoalHeader
            goalId={goalId}
            goals={goals}
            lifeAreas={lifeAreas}
            isLink={headerIsLink}
            onPremium={onPremium}
          />
        )}
        {!!canCollapse && (
          <Styled.IconButtonContainer>
            <IconButton
              icon={ChevronLeft}
              rotation={collapseOpen ? -90 : 0}
              size={IconButtonSizes.Small}
              color={Colors.Default}
              as="span"
            />
          </Styled.IconButtonContainer>
        )}
      </Styled.HeaderContainer>
      <CollapseContainer {...collapseProps}>
        <Styled.ChildrenContainer $hasExtraSpacing={headerHasImage}>
          {children}
        </Styled.ChildrenContainer>
        {!!canCreateTask && (
          <Styled.NewTaskContainer $hasExtraSpacing={headerHasImage}>
            <NewTaskRow
              date={date}
              startTime={startTime}
              duration={duration}
              goal={goalId}
              lifeArea={lifeAreaId}
              parentIds={parentIds}
              parentChildIds={parentChildIds}
              priority={priority}
              newTaskInputRef={newTaskInputRef}
              hideMeta={hideMeta}
              tags={tagIds}
            />
          </Styled.NewTaskContainer>
        )}
        {!!canCreateHabit && (
          <Styled.NewTaskContainer $hasExtraSpacing={headerHasImage}>
            <NewHabitRow
              goal={goalId}
              lifeArea={lifeAreaId}
              priority={priority}
              tags={tagIds}
            />
          </Styled.NewTaskContainer>
        )}
      </CollapseContainer>
    </>
  );
};
